import React, { useEffect, useRef } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "dashjs";
import "videojs-contrib-dash";

// This is the inner component that renders the Video.js player
const VideoJS = ({
  options,
  onReady,
  videoProgress,
  activeContent,
  setVideoDuration
}) => {
  const videoNodeRef = useRef(null);
  const playerRef = useRef(null);
  const totalDataDownloaded = useRef(0);

  // Monitor progress event and estimate data usage
  const handleProgressEvent = (player) => {
    try {
      const buffered = player.buffered();
      if (buffered.length > 0) {
        const currentBufferedEnd = buffered.end(buffered.length - 1);
        const currentBufferedStart = buffered.start(0);
        const totalBufferedTime = currentBufferedEnd - currentBufferedStart;

        // Estimate data usage based on buffered time and bitrate
        const duration = player.duration();
        if (duration > 0) {
          // Get video dimensions to estimate bitrate
          const videoWidth = player.videoWidth();
          const videoHeight = player.videoHeight();

          // Rough bitrate estimation (this is approximate)
          let estimatedBitrate = 1000000; // Default 1 Mbps
          if (videoWidth && videoHeight) {
            // Very rough estimation based on resolution
            const pixels = videoWidth * videoHeight;
            if (pixels > 1920 * 1080) estimatedBitrate = 5000000; // 5 Mbps for 4K+
            else if (pixels > 1280 * 720) estimatedBitrate = 3000000; // 3 Mbps for 1080p
            else if (pixels > 854 * 480) estimatedBitrate = 1500000; // 1.5 Mbps for 720p
            else estimatedBitrate = 800000; // 800 kbps for lower res
          }

          const estimatedDataBytes = (totalBufferedTime * estimatedBitrate) / 8;
          totalDataDownloaded.current = estimatedDataBytes;

          console.log(`📊 Progress Event - Buffered: ${totalBufferedTime.toFixed(2)}s, Estimated: ${(estimatedDataBytes / 1024 / 1024).toFixed(2)} MB`);
        }
      }
    } catch (error) {
      console.error("Error in progress handler:", error);
    }
  };

  useEffect(() => {
    if (!videoNodeRef.current) return;

    if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
   // Reset total data counter
    totalDataDownloaded.current = 0;

    const videoElement = document.createElement("video-js");
    videoElement.classList.add("vjs-big-play-centered");
    videoNodeRef.current.innerHTML = "";
    videoNodeRef.current.appendChild(videoElement);

    const player = videojs(videoElement, options, () => {
      console.log("Player is ready.");
      onReady && onReady(player);
      player.currentTime(activeContent.progressTime || 0);
    });

    playerRef.current = player;

    player.on("timeupdate", () => {
      videoProgress.current = player.currentTime();
    });

    player.on("loadedmetadata", () => {
      setVideoDuration(player.duration()); 
      console.log("Metadata loaded. Setting up progress monitoring...");
    });
  // Use progress event to estimate data usage
    player.on("progress", () => {
      handleProgressEvent(player);
    });

    player.on("ended", () => {
      const dataInKB = totalDataDownloaded.current / 1024;
      const dataInMB = totalDataDownloaded.current / (1024 * 1024);
      const dataInGB = totalDataDownloaded.current / (1024 * 1024 * 1024);

      console.log(`🏁 Video finished. Total data consumed: ${dataInKB.toFixed(2)} KB`);

      if (dataInGB >= 1) {
        console.log(`🏁 Video finished. Total data consumed: ${dataInGB.toFixed(2)} GB`);
      } else {
        console.log(`🏁 Video finished. Total data consumed: ${dataInMB.toFixed(2)} MB`);
      }
    });

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
      }
    };
  }, [options.sources[0].src]);

  return (
    <div data-vjs-player>
      <div ref={videoNodeRef} className="play-and-fullscreen" />
    </div>
  );
};

// This is the main component that renders the player
const VideoRender = ({
  url,
  fileName,
  videoProgress,
  activeContent,
  setVideoDuration, // ✨ NEW PROP
}) => {
  const playerRef = useRef(null);

  const videoJsOptions = {
    autoplay: true,
    controls: true,
    responsive: true,
    fluid: true,
    sources: [
      {
        src: url,
        type: fileName.endsWith(".mpd") ? "application/dash+xml" : "video/mp4",
      },
    ],
    controlBar: {
      fullscreenToggle: true,
    },
  };

  const handlePlayerReady = (player) => {
    playerRef.current = player;
    player.on("waiting", () => {
      console.log("Player is waiting for more data.");
    });
    player.on("dispose", () => {
      console.log("Player will dispose.");
    });
  };

  return (
    <VideoJS
      key={url}
      options={videoJsOptions}
      onReady={handlePlayerReady}
      videoProgress={videoProgress}
      activeContent={activeContent}
      setVideoDuration={setVideoDuration} 
    />
  );
};

export default VideoRender;